import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Clipboard, Search, Camera, Upload, Download, Share2, Calendar } from "lucide-react"
import { Input } from "../components/ui/input.tsx"
import { Button } from "../components/ui/button.tsx"
import { ProfileLayout } from "../components/ProfileLayout"
import { Pagination } from "../components/Pagination"
import api from "../utils/api"
import { getCookie } from "../utils/cookies"

// Helper function to convert string to Title Case
function toTitleCase(str) {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export default function PrescriptionsPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const [dateFilter, setDateFilter] = useState("")
  const [prescriptions, setPrescriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 10

  useEffect(() => {
    const fetchPrescriptions = async () => {
      try {
        const userStr = localStorage.getItem("user") || getCookie("user");
        
        if (!userStr || userStr === "undefined") {
          navigate("/");
          return;
        }
        
        const user = JSON.parse(userStr)
        let mobile = user?.mobile || user?.phone || ""
        if (!mobile) {
          setLoading(false)
          return
        }

        if (!mobile.startsWith("91")) {
          mobile = `91${mobile}`
        }

        const response = await api.get(`/apis/web/prescriptions?mobile=${mobile}`)
        
        if (response.data?.result && Array.isArray(response.data.result)) {
          const mapped = response.data.result.map((item, index) => {
            const trnDate = item.TRNDATE ? new Date(item.TRNDATE) : null;
            return {
              id: item.TRANID || index + 1,
              patientName: toTitleCase((item.PATIENT_NAME || '').trim()),
              mrNumber: item.MRNO || "N/A",
              doctorName: toTitleCase((item.DOCTNAME || item.DOCTOR || '').trim()),
              date: trnDate ? trnDate.toLocaleDateString('en-GB') : (item.DATE || ""),
              isoDate: trnDate ? trnDate.toISOString().split('T')[0] : "", // Used for date filtering
              downloadUrl: item.WHATSAPP_URL || ""
            }
          })
          setPrescriptions(mapped)
        }
      } catch (error) {
        console.error("Error fetching prescriptions:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchPrescriptions()
  }, [])

  const handleLogout = () => {
    localStorage.removeItem("user")
    navigate("/")
  }

  const filteredPrescriptions = prescriptions.filter(p => 
    (p.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
     p.mrNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
     p.doctorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
     p.date.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (dateFilter === "" || p.isoDate === dateFilter)
  )

  return (
    <ProfileLayout onLogout={handleLogout}>
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Clipboard className="w-6 h-6 text-red-600" />
              <h1 className="text-2xl font-bold text-gray-900">Prescriptions</h1>
            </div>
            <Button className="gap-2 w-full sm:w-auto" onClick={() => navigate("/book-now")}>
              <Calendar className="w-4 h-4" />
              Book an Appointment
            </Button>
          </div>
        </div>

        <div className="px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              {/* <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                <div className="relative">
                  <Input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full sm:w-auto"
                  />
                </div>
                {dateFilter && (
                  <Button variant="ghost" onClick={() => setDateFilter("")} className="text-xs">
                    Clear Date
                  </Button>
                )}
              </div> */}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-700">Patient Name</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-700">MR Number</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-700">Doctor</th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-700">Date</th>
                    <th className="px-4 md:px-6 py-3 text-center text-xs md:text-sm font-medium text-gray-700">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                          <p className="text-gray-500">Loading prescriptions...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredPrescriptions.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                        {searchQuery ? `No prescriptions found matching "${searchQuery}"` : "No prescriptions available."}
                      </td>
                    </tr>
                  ) : (
                    filteredPrescriptions.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((prescription) => (
                    <tr key={prescription.id} className="hover:bg-gray-50">
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-900 font-medium">{prescription.patientName}</td>
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600 font-mono">{prescription.mrNumber}</td>
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600">{prescription.doctorName}</td>
                      <td className="px-4 md:px-6 py-4 text-xs md:text-sm text-gray-600">{prescription.date}</td>
                      <td className="px-4 md:px-6 py-4 text-center">
                        {prescription.downloadUrl ? (
                          <a 
                            href={prescription.downloadUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-red-600 hover:text-red-800 transition-colors inline-block"
                          >
                            <Download className="w-5 h-5" />
                          </a>
                        ) : (
                          <span className="text-gray-300">
                            <Download className="w-5 h-5" />
                          </span>
                        )}
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={currentPage} totalPages={Math.ceil(filteredPrescriptions.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
            </div>
          </div>
        </div>
      </div>
    </ProfileLayout>
  )
}
